import createBatchedPromise from 'api/createBatchedPromise'
import { requestLoyaltyEarnPointsCalculationBatch } from 'api/loyalty/requestLoyaltyEarnPointsCalculationBatch'
import type { EarnPointsCalculationOptions } from 'api/mappers/loyalty/mapLoyaltyEarnPointsCalculation'

interface SingleArg {
  request: App.LoyaltyPointsEarnCalculationRequest
  key: string
}

/**
 * Batches individual earn calculation requests into a single batch API call.
 * Requests are accumulated over 32ms and sent together.
 * The 100-item batch limit is handled internally by requestLoyaltyEarnPointsCalculationBatch.
 */
const batchRequestLoyaltyEarnPointsCalculation = createBatchedPromise<
  Map<string, App.LoyaltyPointsEarnCalculation | undefined>,
  App.LoyaltyPointsEarnCalculation,
  SingleArg,
  SingleArg,
  string,
  EarnPointsCalculationOptions
>({
  waitTime: 32,
  argsReducerFn: (accumulated, singleArg) => ({
    ...accumulated,
    [singleArg.key]: singleArg,
  }),
  // createBatchedPromise captures options from the first caller only;
  // all callers in a batch window share the same Redux state so options are identical
  batchRequestFn: async(accumulated, options) => {
    const entries = Object.entries(accumulated)
    const requests = entries.map(([_, { request }]) => request)
    // options is typed as optional due to createBatchedPromise's signature,
    // but is always passed from submitLoyaltyPointsEarnCalculationRequest
    if (!options) {
      throw new Error('EarnPointsCalculationOptions are required for batch earn calculation')
    }
    const results = await requestLoyaltyEarnPointsCalculationBatch(requests, options)

    const resultMap = new Map<string, App.LoyaltyPointsEarnCalculation | undefined>()
    entries.forEach(([key], index) => {
      resultMap.set(key, results[index])
    })
    return resultMap
  },
  singleValueFn: (singleArg, resultMap) => {
    const result = resultMap.get(singleArg.key)
    if (!result) {
      throw new Error(`Loyalty earn calculation failed for key: ${singleArg.key}`)
    }
    return result
  },
})

export default batchRequestLoyaltyEarnPointsCalculation
